// src/lib/contractDerive.js — the figures the advance form COMPUTES rather than asks for.
// PURE, tested by contractDerive.test.mjs.
//
// Owner, 2026-09-13: "received money should be auto filled from the amount of avansi already
// filled on top", and of the main-deal part: "this also should be auto calculated".
//
// What the agent received IS the sum handed over — the total read a second way, not a separate
// question. (Until 1179 this also derived the main-deal part as the total less a prepaid agency
// commission; that composition is barred and its fields are gone — see advanceDerivations.)
// Asking for them invites a composition that does not add up — which is exactly what the
// components check in the composer then refuses, so the form would be arguing with itself.
//
// ⚠️ A figure the agent typed is an ANSWER and is never overwritten afterwards. Same rule as the
//    due day following the lease start: derive until a human takes the wheel, then stop.
// ⚠️ SUBTRACT ONLY WITHIN ONE CURRENCY. A prepaid commission in GEL against a USD total cannot be
//    subtracted without inventing a rate — the rule binder-rules has followed since 2026-08-06.
//    There the part simply stays the whole total and the composer's own check reports the
//    mismatch, which is the honest outcome: a visible disagreement rather than a quiet fiction.

const money = m => (m && typeof m === 'object' && Number.isFinite(Number(m.value)) ? m : null)

// ⚖️ SINCE 1179 THE ADVANCE HAS NO COMMISSION PART (owner 2026-09-14, pending Georgian counsel).
// Civil Code 744–745(2) bar an advance on brokerage remuneration, so the whole advance is for the
// main deal and senTavus may only HOLD part of it — custody, not income. The 1149 composition this
// function used to derive (`part_advance` = total − `part_commission_prepaid`) described exactly the
// barred instrument, and both fields are gone from the template.
// ⚠️ It must not come back through here: a derivation that quietly fills a field the form no longer
//    asks is how a removed legal instrument reappears in a draft without anybody choosing it.

/**
 * @param {object} p
 * @param {string} p.key      the field the agent just edited
 * @param {*}      p.value    its new value
 * @param {object} p.values   the values BEFORE this edit
 * @param {object} p.touched  {fieldKey: true} for figures the agent has set by hand
 * @returns {object} the extra values to merge — empty when nothing derives
 */
export function advanceDerivations({ key, value, values = {}, touched = {} }) {
  if (key !== 'advance_amount') return {}
  const total = money(value)
  if (!total) return {}
  // what the agent received IS the sum handed over — until they say otherwise
  // only when the agent receives the WHOLE advance; "agent_share" is a part the agent types (2026-09-17)
  const out = touched.agent_received_amount || values.advance_receipt_form === 'agent_share' ? {} : { agent_received_amount: { ...total } }
  // the owner-fault refund IS the whole advance — until they lower it (owner 2026-09-17, 1255).
  // ⚠️ `touched` lives only in the open composer, so a draft REOPENED with a lowered refund carries
  //    no mark; the refund also counts as the agent's own when it no longer equals the advance it was
  //    filled from — else editing the advance would silently erase a lowered refund and its reason.
  const prevRefund = money(values.refund_amount), prevTotal = money(values.advance_amount)
  const followsTotal = !prevRefund || prevRefund.value === '' || prevRefund.value == null
    || (prevTotal && Number(prevRefund.value) === Number(prevTotal.value) && (prevRefund.currency || '') === (prevTotal.currency || ''))
  if (!touched.refund_amount && followsTotal) out.refund_amount = { ...total }
  return out
}

export const DERIVED_FROM_TOTAL = ['agent_received_amount', 'refund_amount']

/**
 * 🔴 NOT A DERIVATION — A FORCED VALUE, and the difference is the point (1170).
 *
 * A prepaid commission discharges senTavus's fee under the agency agreement, so it can be credited
 * to nothing else; `prior_payment.js` has always overridden the destination box, and since 1170 so
 * do the save path and the renderer. The form was the last surface still offering the choice, and
 * an answer three layers then discard is a control with nothing behind it.
 *
 * ⚠️ So unlike `advanceDerivations`, this one DOES overwrite a hand-typed answer. The usual rule —
 *    derive until a human takes the wheel — protects a figure the agent is entitled to decide.
 *    This is not one of those: the alternative is not a different answer, it is a lease that
 *    promises a subtraction the arithmetic refuses to make.
 *
 * @returns {object} the values to merge — empty when nothing is forced
 */
export function priorPaymentForced({ key, value }) {
  if (key !== 'prior_payment_kind') return {}
  return String(value || '') === 'prepaid_commission'
    ? { prior_payment_credited_against: 'agency_commission' }
    : {}
}
